<template>
  <page-header-wrapper>
    <a-card :bordered="false">
      <!-- 条件搜索 -->
      <div class="table-page-search-wrapper">
        <a-form layout="inline">
          <a-row :gutter="48">
            <a-col :md="5" :sm="24">
              <a-form-item :label="$t('monitor.operlog.systemModule')">
                <a-input v-model="queryParam.title" :placeholder="$t('monitor.operlog.inputSystemModule')" allow-clear/>
              </a-form-item>
            </a-col>
            <a-col :md="5" :sm="24">
              <a-form-item :label="$t('monitor.operlog.operName')">
                <a-input v-model="queryParam.operName" :placeholder="$t('monitor.operlog.inputOperName')" allow-clear/>
              </a-form-item>
            </a-col>
            <template v-if="advanced">
              <a-col :md="5" :sm="24">
                <a-form-item :label="$t('monitor.operlog.filterType')">
                  <a-select :placeholder="$t('monitor.operlog.businessTypePlaceholder')" v-model="queryParam.businessType" style="width: 100%" allow-clear>
                    <a-select-option v-for="(d, index) in sysOperTypeOptions" :key="index" :value="d.value">{{ d.label }}</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :md="5" :sm="24">
                <a-form-item :label="$t('system.status')">
                  <a-select :placeholder="$t('monitor.operlog.statusPlaceholder')" v-model="queryParam.status" style="width: 100%" allow-clear>
                    <a-select-option v-for="(d, index) in sysCommonStatusOptions" :key="index" :value="d.value">{{ d.label }}</a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col :md="5" :sm="24">
                <a-form-item :label="$t('monitor.operlog.operTime')">
                  <a-range-picker style="width: 100%" v-model="dateRange" valueFormat="YYYY-MM-DD" format="YYYY-MM-DD" allow-clear/>
                </a-form-item>
              </a-col>
            </template>
            <a-col :md="!advanced && 8 || 24" :sm="24">
              <span class="table-page-search-submitButtons" :style="advanced && { float: 'right', overflow: 'hidden' } || {} ">
                <a-button type="primary" @click="handleQuery"><a-icon type="search" />{{ $t('common.query') }}</a-button>
                <a-button style="margin-left: 8px" @click="resetQuery"><a-icon type="redo" />{{ $t('common.reset') }}</a-button>
                <a @click="toggleAdvanced" style="margin-left: 8px">
                  {{ advanced ? $t('system.collapse') : $t('system.expand') }}
                  <a-icon :type="advanced ? 'up' : 'down'"/>
                </a>
              </span>
            </a-col>
          </a-row>
        </a-form>
      </div>
      <!-- 操作 -->
      <div class="table-operations">
        <a-button type="danger" :disabled="multiple" @click="handleDelete" v-hasPermi="['monitor:operlog:remove']">
          <a-icon type="delete" />{{ $t('common.delete') }}
        </a-button>
        <a-button type="danger" @click="handleClean" v-hasPermi="['monitor:operlog:remove']">
          <a-icon type="delete" />{{ $t('monitor.operlog.clean') }}
        </a-button>
        <a-button type="primary" @click="handleExport" v-hasPermi="['system:config:export']">
          <a-icon type="download" />{{ $t('common.export') }}
        </a-button>
        <table-setting
          :style="{float: 'right'}"
          :table-size.sync="tableSize"
          v-model="columns"
          :refresh-loading="loading"
          @refresh="getList" />
      </div>
      <!-- 详细信息 -->
      <view-form ref="viewForm" />
      <!-- 数据展示 -->
      <a-table
        :loading="loading"
        :size="tableSize"
        rowKey="operId"
        :columns="columns"
        :data-source="list"
        :row-selection="{ selectedRowKeys: selectedRowKeys, onChange: onSelectChange }"
        :pagination="false"
        :bordered="tableBordered"
        @change="handleTableChange"
      >
        <span slot="businessType" slot-scope="text, record">
          <dict-tag :options="sysOperTypeOptions" :value="record.businessType" />
        </span>
        <span slot="status" slot-scope="text, record">
          <dict-tag :options="sysCommonStatusOptions" :value="record.status" />
        </span>
        <span slot="title" slot-scope="text, record">
          {{ formatOperlogTitle(record.title) }}
        </span>
        <span slot="operation" slot-scope="text, record">
          <a @click="$refs.viewForm.handleView(record)" v-hasPermi="['monitor:operlog:query']">
            <a-icon type="eye" />{{ $t('monitor.operlog.detail') }}
          </a>
        </span>
      </a-table>
      <!-- 分页 -->
      <a-pagination
        class="ant-table-pagination"
        show-size-changer
        show-quick-jumper
        :current="queryParam.pageNum"
        :total="total"
        :page-size="queryParam.pageSize"
        :showTotal="total => $t('table.total', { total })"
        @showSizeChange="onShowSizeChange"
        @change="changeSize"
      />
    </a-card>
  </page-header-wrapper>
</template>

<script>

import { list, delOperlog, cleanOperlog } from '@/api/monitor/operlog'
import ViewForm from './modules/ViewForm'
import { tableMixin } from '@/store/table-mixin'
import { formatOperlogTitle } from '@/utils/operlogTitle'
import { translateDictOptions } from '@/utils/dictI18n'

export default {
  name: 'Operlog',
  components: {
    ViewForm
  },
  mixins: [tableMixin],
  dicts: ['sys_common_status', 'sys_oper_type'],
  data () {
    return {
      list: [],
      selectedRowKeys: [],
      selectedRows: [],
      advanced: false,
      loading: false,
      multiple: true,
      total: 0,
      dateRange: [],
      queryParam: {
        pageNum: 1,
        pageSize: 10,
        title: undefined,
        operName: undefined,
        businessType: undefined,
        status: undefined
      }
    }
  },
  created () {
    this.getList()
  },
  computed: {
    sysCommonStatusOptions () {
      // 依赖 locale，切换语言时重新计算
      const _locale = this.$i18n.locale
      return translateDictOptions(this.dict.type.sys_common_status, 'sys_common_status', this.$t.bind(this))
    },
    sysOperTypeOptions () {
      const _locale = this.$i18n.locale
      return translateDictOptions(this.dict.type.sys_oper_type, 'sys_oper_type', this.$t.bind(this))
    },
    columns () {
      return [
        {
          title: this.$t('monitor.operlog.operId'),
          dataIndex: 'operId',
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.systemModule'),
          dataIndex: 'title',
          scopedSlots: { customRender: 'title' },
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.businessType'),
          dataIndex: 'businessType',
          scopedSlots: { customRender: 'businessType' },
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.requestMethod'),
          dataIndex: 'requestMethod',
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.operName'),
          dataIndex: 'operName',
          align: 'center',
          sorter: true
        },
        {
          title: this.$t('monitor.operlog.host'),
          dataIndex: 'operIp',
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.operLocation'),
          dataIndex: 'operLocation',
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.operStatus'),
          dataIndex: 'status',
          scopedSlots: { customRender: 'status' },
          align: 'center'
        },
        {
          title: this.$t('monitor.operlog.operDate'),
          dataIndex: 'operTime',
          align: 'center',
          sorter: true
        },
        {
          title: this.$t('common.operation'),
          dataIndex: 'operation',
          scopedSlots: { customRender: 'operation' },
          align: 'center'
        }
      ]
    }
  },
  methods: {
    formatOperlogTitle (title) {
      return formatOperlogTitle(title, this.$t.bind(this))
    },
    handleTableChange (pagination, filters, sorter) {
      const sort = this.tableSorter(sorter)
      this.queryParam.orderByColumn = sort.orderByColumn
      this.queryParam.isAsc = sort.isAsc
      this.getList()
    },
    getList () {
      this.loading = true
      list(this.addDateRange(this.queryParam, this.dateRange)).then(response => {
        this.list = response.data.records
        this.total = response.data.total
        this.loading = false
      })
    },
    handleQuery () {
      this.queryParam.pageNum = 1
      this.getList()
    },
    resetQuery () {
      this.dateRange = []
      this.queryParam = {
        pageNum: 1,
        pageSize: 10,
        title: undefined,
        operName: undefined,
        businessType: undefined,
        status: undefined
      }
      this.handleQuery()
    },
    onShowSizeChange (current, pageSize) {
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    changeSize (current, pageSize) {
      this.queryParam.pageNum = current
      this.queryParam.pageSize = pageSize
      this.getList()
    },
    onSelectChange (selectedRowKeys, selectedRows) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedRows = selectedRows
      this.ids = this.selectedRows.map(item => item.operId)
      this.multiple = !selectedRowKeys.length
    },
    toggleAdvanced () {
      this.advanced = !this.advanced
    },
    handleDelete (row) {
      const that = this
      const operIds = row.operId || this.ids
      this.$confirm({
        title: this.$t('system.confirmDelete'),
        content: this.$t('monitor.operlog.confirmDeleteContent', { ids: operIds }),
        onOk () {
          return delOperlog(operIds)
            .then(() => {
              that.onSelectChange([], [])
              that.getList()
              that.$message.success(that.$t('common.deleteSuccess'), 3)
            })
        },
        onCancel () {}
      })
    },
    handleClean () {
      const that = this
      this.$confirm({
        title: this.$t('monitor.operlog.confirmClean'),
        content: this.$t('monitor.operlog.confirmCleanContent'),
        onOk () {
          return cleanOperlog()
            .then(() => {
              that.onSelectChange([], [])
              that.getList()
              that.$message.success(that.$t('monitor.operlog.cleanSuccess'), 3)
            })
        },
        onCancel () {}
      })
    },
    handleExport () {
      const that = this
      this.$confirm({
        title: this.$t('system.confirmExport'),
        content: this.$t('system.confirmExportContent'),
        onOk () {
          that.download('monitor/operlog/export', {
            ...that.queryParam
          }, `operlog_${new Date().getTime()}.xlsx`)
        },
        onCancel () {}
      })
    }
  }
}
</script>
